import InlineAlert from '../Alert/InlineAlert'
import { FunctionComponent } from 'react'
import { CarbonAccountSummary } from '../../../types/CarbonAccountSummary'
import { formatAdaptiveNumber } from '../../../lib/utils'

type IEmissionsSummaryPanelModule = {
	carbonSummary: CarbonAccountSummary
	totalEmissions: number
	/**
	 * Text appended after "kgCO₂e / " in the intensity ratio unit label, e.g.
	 * `${currencySymbol} of turnover` or `${currencySymbol} unit project value`.
	 * The currency symbol itself must be resolved by the caller from org/company settings.
	 */
	intensityRatioUnit: string
	activeScope?: '' | '1' | '2' | '3'
	locale?: string
	loading?: boolean
	error?: string
}

/**
 * Canonical emissions summary panel: Total Emissions + Intensity Ratio, Scope 1 (Direct
 * Emissions), and Scope 2 & 3 (Indirect Emissions, sharing a spanning header). Completion
 * percentages are price-weighted and must come from the backend
 * (see `backend/app/lib/calculations/countCompleted.ts`) — never recomputed on the frontend
 * except by mirroring that same formula for pages without a `CarbonAccountSummary`.
 *
 * `activeScope` never hides a section - geometry stays constant so the panel keeps matching
 * the width of the 4-tab TabMenu below it. Instead, the inactive section's heading and value
 * text fade to the same grey already used for its own labels/units.
 */
const EmissionsSummaryPanelModule: FunctionComponent<IEmissionsSummaryPanelModule> = ({
	carbonSummary,
	totalEmissions,
	intensityRatioUnit,
	activeScope = '',
	locale = 'en-GB',
	loading = false,
	error,
}) => {
	const scope1Percentage = Math.round(Number(carbonSummary.scope1Completion)) || 0
	const scope2Percentage = Math.round(Number(carbonSummary.scope2Completion)) || 0
	const scope3Percentage = Math.round(Number(carbonSummary.scope3Completion)) || 0

	const intensityValue = carbonSummary.localIntensityRatio

	const overviewActive = activeScope === ''

	// Overview card (first card): intensity values colored only in Overview mode
	const overviewTextColor = overviewActive ? 'text-white' : 'text-charcoal-300'

	// Apply fire palette colors to emissions values in cards 2, 3, and 4.
	// When a specific scope tab is active, only that scope shows its fire color;
	// all others are muted to charcoal-300. When Overview is active, all scopes
	// show their fire palette colors.
	const scope1ValueClass = (activeScope === '' || activeScope === '1') ? 'text-fire-800' : 'text-charcoal-300'
	const scope2ValueClass = (activeScope === '' || activeScope === '2') ? 'text-ozone-500' : 'text-charcoal-300'
	const scope3ValueClass = (activeScope === '' || activeScope === '3') ? 'text-ozone-500' : 'text-charcoal-300'

	// Progress bar fill classes - same fire/charcoal tokens as the emissions value colors above,
	// expressed as Tailwind classes (not inline styles) so there is one source of truth for colour.
	const scope1ProgressBarClass = (activeScope === '' || activeScope === '1') ? 'bg-fire-800' : 'bg-charcoal-300'
	const scope2ProgressBarClass = (activeScope === '' || activeScope === '2') ? 'bg-ozone-500' : 'bg-charcoal-300'
	const scope3ProgressBarClass = (activeScope === '' || activeScope === '3') ? 'bg-ozone-500' : 'bg-charcoal-300'

	if (loading) {
		return (
			<div
				data-testid='emissions-summary-panel-loading'
				className='flex rounded-lg overflow-hidden mt-5 animate-pulse'
			>
				<div className='flex flex-col grow p-4 bg-charcoal-700 h-28' />
				<div className='flex flex-col grow p-4 bg-charcoal-600 h-28' />
				<div className='flex flex-col grow p-4 bg-charcoal-700 h-28' />
				<div className='flex flex-col grow p-4 bg-charcoal-600 h-28' />
			</div>
		)
	}

	if (error) {
		return (
			<InlineAlert type='error' className='mt-4' data-testid='emissions-summary-panel-error'>				<p className='text-white'>
					There was an error fetching the carbon summary for this accounting period. If this
					problem persists, please contact the Neoni team.
				</p>
				<p className='text-white'>{error}</p>
			</InlineAlert>
		)
	}

	// Progress bar: track is h-1, fill is h-3 so it reads as a thicker line than its track.
	// The fill is absolutely centred on the track's vertical midpoint via top-1/2 + -translate-y-1/2,
	// since a taller child can't be centred with block layout alone.
	// PROGRESS_BAR_HEIGHT_PX mirrors the h-3 utility (0.75rem = 12px) so the minWidth keeps the
	// fill a perfect circle at low percentages - the two must stay in lockstep if h-3 ever changes.
	const PROGRESS_BAR_HEIGHT_PX = 12
	const ProgressBar: FunctionComponent<{
		percentage: number
		barColorClass: string
	}> = ({ percentage, barColorClass }) => (
		<div className='relative w-full h-1 bg-charcoal-300 rounded-full'>
			<div
				className={`absolute top-1/2 left-0 -translate-y-1/2 h-3 rounded-full transition-all duration-300 ${barColorClass}`}
				style={{
					width: `${percentage}%`,
					minWidth: percentage > 0 ? `${PROGRESS_BAR_HEIGHT_PX}px` : '0px',
				}}
			/>
		</div>
	)

	return (
		<div data-testid='emissions-summary-panel'>
			{/*
				Table-like row structure: The outer grid creates 4 columns and N rows.
				Each row contains one visual element per card, naturally aligned by the grid.
				This ensures all emissions values sit in the same row and align perfectly,
				regardless of how secondary headings wrap at different breakpoints.
			*/}
			<div
				className='grid grid-cols-4 rounded-lg overflow-hidden mt-5'
				data-testid='emissions-summary'
			>
				{/* ROW 1: Main headings */}
				<div className='bg-charcoal-800 px-4 pt-2 pb-2 text-center min-w-0'>
					<span className='text-xs text-charcoal-300 truncate block'>TOTAL EMISSIONS</span>
				</div>
				<div className='bg-charcoal-700 px-4 pt-2 pb-2 text-center min-w-0'>
					<span className='text-xs text-charcoal-300 truncate block'>DIRECT EMISSIONS</span>
				</div>
				<div className='col-span-2 bg-charcoal-600 px-4 pt-2 pb-2 text-center min-w-0'>
					<span className='text-xs text-charcoal-300 truncate block'>INDIRECT EMISSIONS</span>
				</div>

				{/* ROW 2: Secondary scope descriptions */}
				<div className='bg-charcoal-800 px-4 py-0 min-w-0'>
					{/* Empty for card 1, but still present to reserve space */}
					<div className='text-xs text-charcoal-300 text-center truncate'>
						&nbsp;
					</div>
				</div>
				<div className='bg-charcoal-700 px-4 py-0 min-w-0'>
					<div className='text-xs text-charcoal-300 text-center truncate'>
						(Burning fuels, releasing gases)
					</div>
				</div>
				<div className='bg-charcoal-600 px-4 py-0 min-w-0'>
					<div className='text-xs text-charcoal-300 text-center truncate'>
						(Purchased energy, heat, cooling)
					</div>
				</div>
				<div className='bg-charcoal-600 px-4 py-0 min-w-0'>
					<div className='text-xs text-charcoal-300 text-center truncate'>
						(Rest of supply chain)
					</div>
				</div>

				{/* ROW 3: Emissions values (all in same row = perfect vertical alignment) */}
				<div className='bg-charcoal-800 px-4 py-2 min-w-0'>
					<div className='text-right'>
					<div className={`text-3xl ${overviewTextColor}`}>
							{formatAdaptiveNumber(totalEmissions ?? 0, locale)}
						</div>
						<div className='text-sm text-charcoal-300 truncate'>kgCO₂e</div>
					</div>
				</div>
				<div className='bg-charcoal-700 px-4 py-2 min-w-0'>
					<div className='text-right'>
					<div className={`text-3xl ${scope1ValueClass}`}>
							{formatAdaptiveNumber(Number(carbonSummary.scope1Total) || 0, locale)}
						</div>
						<div className='text-sm text-charcoal-300 truncate'>kgCO₂e</div>
					</div>
				</div>
				<div className='bg-charcoal-600 px-4 py-2 min-w-0'>
					<div className='text-right'>
					<div className={`text-3xl ${scope2ValueClass}`}>
							{formatAdaptiveNumber(Number(carbonSummary.scope2Total) || 0, locale)}
						</div>
						<div className='text-sm text-charcoal-300 truncate'>kgCO₂e</div>
					</div>
				</div>
				<div className='bg-charcoal-600 px-4 py-2 min-w-0'>
					<div className='text-right'>
					<div className={`text-3xl ${scope3ValueClass}`}>
							{formatAdaptiveNumber(Number(carbonSummary.scope3Total) || 0, locale)}
						</div>
						<div className='text-sm text-charcoal-300 truncate'>kgCO₂e</div>
					</div>
				</div>

				{/* ROW 4: Intensity ratio value (card 1) + Progress bars (cards 2,3,4) on same row */}
				<div className='bg-charcoal-800 px-4 pt-2 pb-1 min-w-0 flex flex-col justify-center'>
					<div className='text-right'>
						<div className={`text-2xl leading-none ${overviewTextColor}`}>
							{intensityValue !== null && intensityValue !== ''
								? formatAdaptiveNumber(Number(intensityValue), locale)
							: '-'}
						</div>
					</div>
				</div>
				<div className='bg-charcoal-700 px-4 pt-2 pb-1 min-w-0 flex flex-col justify-center'>
					<ProgressBar
						percentage={scope1Percentage}
						barColorClass={scope1ProgressBarClass}
					/>
				</div>
				<div className='bg-charcoal-600 px-4 pt-2 pb-1 min-w-0 flex flex-col justify-center'>
					<ProgressBar
						percentage={scope2Percentage}
						barColorClass={scope2ProgressBarClass}
					/>
				</div>
				<div className='bg-charcoal-600 px-4 pt-2 pb-1 min-w-0 flex flex-col justify-center'>
					<ProgressBar
						percentage={scope3Percentage}
						barColorClass={scope3ProgressBarClass}
					/>
				</div>

				{/* ROW 5: Intensity ratio unit label (card 1) + progress bar percentage text (cards 2,3,4) on same row */}
				<div className='bg-charcoal-800 px-4 pt-1 pb-2 min-w-0 flex flex-col justify-center'>
					<div className='text-right'>
						<div className='text-xs text-charcoal-300 truncate'>
							kgCO₂e / {intensityRatioUnit}
						</div>
					</div>
				</div>
				<div className='bg-charcoal-700 px-4 pt-1 pb-2 min-w-0 flex flex-col justify-center'>
					<div className='text-xs text-charcoal-300 truncate'>
						<span>{scope1Percentage}%</span>
						<span> complete</span>
					</div>
				</div>
				<div className='bg-charcoal-600 px-4 pt-1 pb-2 min-w-0 flex flex-col justify-center'>
					<div className='text-xs text-charcoal-300 truncate'>
						<span>{scope2Percentage}%</span>
						<span> complete</span>
					</div>
				</div>
				<div className='bg-charcoal-600 px-4 pt-1 pb-2 min-w-0 flex flex-col justify-center'>
					<div className='text-xs text-charcoal-300 truncate'>
						<span>{scope3Percentage}%</span>
						<span> complete</span>
					</div>
				</div>
			</div>
		</div>
	)
}

export default EmissionsSummaryPanelModule
